import { SocialLogin, type GoogleLoginResponseOnline } from '@capgo/capacitor-social-login';
import { ref } from 'vue';

export interface GoogleLoginResponse {
  idToken: string;
  accessToken?: string;
  profile?: {
    email: string;
    familyName: string;
    givenName: string;
    id: string;
    name: string;
    imageUrl?: string;
  };
}

export interface GoogleLoginError {
  message: string;
  code?: string;
}

const isInitialized = ref(false);

export function useGoogleLogin() {
  const isLoggingIn = ref(false);
  const error = ref<GoogleLoginError | null>(null);

  /**
   * Initialize the Google Social Login plugin
   * This should be called once when the app starts
   * @param webClientId - The Web Client ID from Google Cloud Console (for Android and Web)
   * @param iosClientId - Optional iOS Client ID from Google Cloud Console (for iOS)
   */
  async function initialize(webClientId: string, iosClientId?: string): Promise<void> {
    if (isInitialized.value) {
      console.log('[useGoogleLogin] Already initialized');
      return;
    }

    try {
      console.log('[useGoogleLogin] Initializing Google Login');
      console.log('[useGoogleLogin] Web Client ID:', webClientId);
      console.log('[useGoogleLogin] iOS Client ID:', iosClientId || 'Not provided');

      // Build config object - only include iOS Client ID if it's a valid string
      const googleConfig: {
        webClientId: string;
        iOSClientId?: string;
        mode: 'online' | 'offline';
      } = {
        webClientId,
        mode: 'online', // Default mode for most apps
      };

      // Only add iOS Client ID if it's provided and not empty
      if (iosClientId && iosClientId.trim().length > 0) {
        googleConfig.iOSClientId = iosClientId;
        console.log('[useGoogleLogin] iOS Client ID will be used');
      } else {
        console.log('[useGoogleLogin] iOS Client ID not provided, will work on Web/Android only');
      }

      await SocialLogin.initialize({
        google: googleConfig,
      });
      isInitialized.value = true;
      console.log('[useGoogleLogin] Initialized successfully');
    } catch (err) {
      console.error('[useGoogleLogin] Initialization failed:', err);
      error.value = {
        message: 'Failed to initialize Google login',
        code: 'INIT_FAILED',
      };
      throw err;
    }
  }

  /**
   * Trigger Google login flow
   * @returns GoogleLoginResponse with idToken and user profile
   */
  async function login(): Promise<GoogleLoginResponse> {
    if (!isInitialized.value) {
      const errorMsg = 'Google Social Login not initialized. Call initialize() first.';
      console.error('[useGoogleLogin]', errorMsg);
      error.value = {
        message: errorMsg,
        code: 'NOT_INITIALIZED',
      };
      throw new Error(errorMsg);
    }

    isLoggingIn.value = true;
    error.value = null;

    try {
      console.log('[useGoogleLogin] Starting login flow');
      const result = await SocialLogin.login({
        provider: 'google',
        options: {},
      });

      console.log('[useGoogleLogin] Login successful:', {
        hasResult: !!result,
      });

      // The Capgo plugin returns the response in result.result
      const googleData = result?.result as GoogleLoginResponseOnline;

      if (!googleData || !googleData.idToken) {
        throw new Error('No idToken received from Google login');
      }

      const response: GoogleLoginResponse = {
        idToken: googleData.idToken,
        accessToken: typeof googleData.accessToken === 'string' ? googleData.accessToken : undefined,
        profile: googleData.profile
          ? {
              email: googleData.profile.email || '',
              familyName: googleData.profile.familyName || '',
              givenName: googleData.profile.givenName || '',
              id: googleData.profile.id || '',
              name: googleData.profile.name || '',
              imageUrl: googleData.profile.imageUrl || undefined,
            }
          : undefined,
      };

      return response;
    } catch (err: unknown) {
      console.error('[useGoogleLogin] Login failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Google login failed';
      const errorCode = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : 'LOGIN_FAILED';
      error.value = {
        message: errorMessage,
        code: errorCode,
      };
      throw err;
    } finally {
      isLoggingIn.value = false;
    }
  }

  /**
   * Logout from Google (if needed)
   */
  async function logout(): Promise<void> {
    if (!isInitialized.value) {
      console.warn('[useGoogleLogin] Cannot logout - not initialized');
      return;
    }

    try {
      console.log('[useGoogleLogin] Logging out');
      await SocialLogin.logout({
        provider: 'google',
      });
      console.log('[useGoogleLogin] Logged out successfully');
    } catch (err) {
      console.error('[useGoogleLogin] Logout failed:', err);
      // Don't throw - logout failures are often not critical
    }
  }

  return {
    initialize,
    login,
    logout,
    isLoggingIn,
    isInitialized,
    error,
  };
}
